import { ReactNode, useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { StreamingDots, useStreamingStyles } from "./StreamingStatus";

interface CollapsibleDivProps {
  children?: ReactNode;
  // Pre-split analysis steps. Each entry renders as its own row; falling back
  // to `children` keeps older callers working with a single row.
  steps?: ReactNode[];
  // True while INFO messages are still streaming into this block.
  active?: boolean;
  // Rendered under the steps when expanded (supporting sources).
  footer?: ReactNode;
}

const STYLE_ID = "analysis-steps-styles";

// One stylesheet for every collapsible; keyframes cannot live in inline styles.
const STYLES = `
.as-root { margin: 6px 0; }
.as-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  background: none;
  border: 0;
  font: inherit;
  font-size: 0.92em;
  line-height: 1.5;
  color: rgba(232, 237, 244, 0.52);
  cursor: pointer;
  transition: color 140ms ease;
}
.as-toggle:hover { color: rgba(232, 237, 244, 0.86); }
.as-toggle:focus-visible {
  outline: 1px solid rgba(232, 237, 244, 0.35);
  outline-offset: 3px;
  border-radius: 4px;
}
.as-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.as-chevron {
  flex: none;
  opacity: 0.65;
  transition: transform 160ms ease;
}
.as-chevron[data-open="true"] { transform: rotate(90deg); }

.as-elapsed {
  flex: none;
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
}

.as-panel {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition: grid-template-rows 180ms ease, opacity 140ms ease;
}
.as-panel[data-open="true"] {
  grid-template-rows: 1fr;
  opacity: 1;
}
.as-panel-inner { overflow: hidden; min-height: 0; }

.as-list {
  margin: 8px 0 2px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 10px;
  overflow: hidden;
}
.as-step {
  padding: 9px 12px;
  font-size: 0.92em;
  line-height: 1.5;
  color: rgba(232, 237, 244, 0.5);
}
.as-step + .as-step { border-top: 1px solid rgba(255, 255, 255, 0.07); }
.as-step > :first-child { margin-top: 0; }
.as-step > :last-child { margin-bottom: 0; }
.as-footer { padding: 0 12px 10px; }

@media (prefers-reduced-motion: reduce) {
  .as-panel { transition: none; }
  .as-chevron { transition: none; }
}
`;

const useStyleSheet = () => {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
  }, []);
};

// The visible text of a step, used for the live header label.
const nodeText = (node: any): string => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (node?.props?.children !== undefined) return nodeText(node.props.children);
  return "";
};

const formatElapsed = (seconds: number) =>
  seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

const CollapsibleDiv: React.FC<CollapsibleDivProps> = ({
  children,
  steps,
  active = false,
  footer,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [elapsed, setElapsed] = useState<number | null>(null);
  const startedAtRef = useRef<number | null>(null);
  useStyleSheet();
  useStreamingStyles();

  // Ticks while steps stream in, then freezes on the final duration so the
  // header still reports how long the analysis took.
  useEffect(() => {
    if (!active) return;
    if (startedAtRef.current == null) {
      startedAtRef.current = Date.now();
      setElapsed(0);
    }
    const started = startedAtRef.current;
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  const rows =
    steps && steps.length ? steps : children != null ? [children] : [];

  const liveLabel = active
    ? nodeText(rows[rows.length - 1]).replace(/\s+/g, " ").trim()
    : "";
  const label = liveLabel
    ? liveLabel
    : rows.length > 1
      ? `Analysis steps · ${rows.length}`
      : "Analysis steps";

  return (
    <div className="as-root">
      <button
        type="button"
        className="as-toggle"
        data-active={active ? "true" : "false"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {active && <StreamingDots />}
        <span className={active ? "as-label ss-shimmer" : "as-label"}>
          {label}
        </span>
        {elapsed != null && (
          <span className="as-elapsed">{formatElapsed(elapsed)}</span>
        )}
        <ChevronRight
          className="as-chevron"
          data-open={isOpen ? "true" : "false"}
          size={14}
          aria-hidden="true"
        />
      </button>

      <div className="as-panel" data-open={isOpen ? "true" : "false"}>
        <div className="as-panel-inner">
          <div className="as-list">
            {rows.map((row, index) => (
              <div className="as-step" key={index}>
                {row}
              </div>
            ))}
            {footer ? <div className="as-footer">{footer}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollapsibleDiv;
